import { Link, useNavigate } from 'react-router-dom';
import { SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useGeo } from '../contexts/GeoContext';
import InternationalShippingNotice from './InternationalShippingNotice';
import { thumb as optThumb } from '../lib/imageUrl';

export default function CartSheet() {
  const { cart, loading, updateCartItem, removeFromCart, setCartOpen } = useCart();
  const { user } = useAuth();
  const { formatPrice } = useGeo();
  const navigate = useNavigate();

  const handleCheckout = () => {
    setCartOpen(false);
    navigate('/checkout');
  };

  const handleQuantityChange = async (index, delta) => {
    const item = cart.items[index];
    const newQty = item.quantity + delta;
    if (newQty > 0) {
      await updateCartItem(index, newQty);
    } else {
      await removeFromCart(index);
    }
  };

  if (!user) {
    return (
      <div className="h-full flex flex-col">
        <SheetHeader className="p-6 border-b-2 border-[#0A0A0A]">
          <SheetTitle className="font-bold text-xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Shopping Bag
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <ShoppingBag className="w-16 h-16 mb-4 text-[#525252]" />
          <p className="text-lg font-semibold mb-2">Sign in to view your bag</p>
          <p className="text-[#525252] mb-6">Your shopping bag is waiting for you</p>
          <Button
            onClick={() => {
              setCartOpen(false);
              navigate('/login');
            }}
            data-testid="cart-login-btn"
            className="bg-[#0A0A0A] text-white border-2 border-[#0A0A0A] rounded-none hover:translate-y-[-2px] hover:shadow-[4px_4px_0px_rgba(0,0,0,0.3)] transition-all"
          >
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="h-full flex flex-col">
        <SheetHeader className="p-6 border-b-2 border-[#0A0A0A]">
          <SheetTitle className="font-bold text-xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Shopping Bag
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <ShoppingBag className="w-16 h-16 mb-4 text-[#525252]" />
          <p className="text-lg font-semibold mb-2">Your bag is empty</p>
          <p className="text-[#525252] mb-6">Looks like you haven't added anything yet</p>
          <Button
            onClick={() => {
              setCartOpen(false);
              navigate('/products');
            }}
            data-testid="cart-shop-btn"
            className="bg-[#0A0A0A] text-white border-2 border-[#0A0A0A] rounded-none hover:translate-y-[-2px] hover:shadow-[4px_4px_0px_rgba(0,0,0,0.3)] transition-all"
          >
            Start Shopping
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <SheetHeader className="p-6 border-b-2 border-[#0A0A0A]">
        <SheetTitle className="font-bold text-xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Shopping Bag ({cart.items.length})
        </SheetTitle>
      </SheetHeader>

      <div className="flex-1 overflow-y-auto">
        {cart.items.map((item, index) => (
          <div
            key={`${item.product_id}-${item.size}-${item.color}-${index}`}
            className="p-4 border-b-2 border-[#0A0A0A]"
            data-testid={`cart-item-${index}`}
          >
            <div className="flex gap-4">
              {/* Product Image */}
              <div className="w-20 h-24 bg-[#F5F5F5] border-2 border-[#0A0A0A] overflow-hidden flex-shrink-0">
                {item.product?.images?.[0] ? (
                  <img
                    src={optThumb(item.product.images[0])}
                    alt={item.product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#525252]">
                    No Image
                  </div>
                )}
              </div>

              {/* Product Details */}
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm truncate">{item.product?.name}</h4>
                <p className="text-xs text-[#525252] mt-1">
                  {item.size && `Size: ${item.size}`}
                  {item.size && item.color && ' | '}
                  {item.color && `Color: ${item.color}`}
                </p>
                <p className="font-bold mt-2">{formatPrice(item.item_total || 0)}</p>

                {/* Quantity Controls */}
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={() => handleQuantityChange(index, -1)}
                    disabled={loading}
                    data-testid={`cart-item-${index}-decrease`}
                    className="quantity-btn"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-8 text-center font-semibold">{item.quantity}</span>
                  <button
                    onClick={() => handleQuantityChange(index, 1)}
                    disabled={loading}
                    data-testid={`cart-item-${index}-increase`}
                    className="quantity-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => removeFromCart(index)}
                    disabled={loading}
                    data-testid={`cart-item-${index}-remove`}
                    className="ml-auto p-2 hover:bg-[#FAD4D4] transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="border-t-2 border-[#0A0A0A] p-6 bg-white">
        <div className="mb-3">
          <InternationalShippingNotice variant="inline" />
        </div>
        <div className="flex justify-between items-center mb-4">
          <span className="font-semibold">Subtotal</span>
          <span className="font-bold text-xl">{formatPrice(cart.total || 0)}</span>
        </div>
        <Button
          onClick={handleCheckout}
          data-testid="cart-checkout-btn"
          className="w-full bg-[#0A0A0A] text-white border-2 border-[#0A0A0A] rounded-none h-12 font-semibold text-base hover:translate-y-[-2px] hover:shadow-[4px_4px_0px_rgba(0,0,0,0.3)] transition-all"
        >
          Checkout
        </Button>
        <Button
          onClick={() => {
            setCartOpen(false);
            navigate('/products');
          }}
          variant="outline"
          className="w-full mt-2 border-2 border-[#0A0A0A] rounded-none h-12 font-semibold text-base hover:bg-[#FAD4D4] hover:shadow-[4px_4px_0px_#0A0A0A] transition-all"
        >
          Continue Shopping
        </Button>
      </div>
    </div>
  );
}
